import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { ScenarioIncident } from '../../types/ScenarioManagement';
import { StandardButton } from '../ui';

interface IncidentTableRowProps {
  incident: ScenarioIncident;
  index: number;
  onEdit: (incident: ScenarioIncident) => void;
  onDelete: (incidentId: string) => void;
  onToggle: (incidentId: string, enabled: boolean) => void;
  onReorder: (incidentId: string, newOrder: number) => void;
  readOnly?: boolean;
  totalIncidents: number;
  getSeverityColor: (severity: string) => string;
  formatTimeLimit: (seconds: number) => string;
}

export const IncidentTableRow: React.FC<IncidentTableRowProps> = React.memo(({
  incident,
  index,
  onEdit,
  onDelete,
  onToggle,
  onReorder,
  readOnly = false,
  totalIncidents,
  getSeverityColor,
  formatTimeLimit
}) => {
  const { isDark } = useTheme();

  return (
    <tr className={`${
      isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'
    } ${!incident.enabled && 'opacity-60'}`}>
      <td className="px-6 py-4 whitespace-nowrap">
        <input
          type="checkbox"
          checked={incident.enabled !== false}
          onChange={(e) => !readOnly && onToggle(incident.id, e.target.checked)}
          disabled={readOnly}
          className={`rounded focus:ring-2 ${
            isDark 
              ? 'border-white/20 text-professional-blue-400 focus:ring-professional-blue-400' 
              : 'border-gray-300 text-blue-600 focus:ring-blue-500'
          } ${readOnly ? 'cursor-not-allowed opacity-50' : ''}`}
          style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}
        />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {readOnly ? (
          <span className={`w-12 px-2 py-1 text-center rounded text-sm inline-block ${
            isDark 
              ? 'bg-white/[0.05] border-white/10 text-white' 
              : 'bg-gray-50 border-gray-200 text-gray-900'
          } border`}>
            {incident.order ?? index}
          </span>
        ) : (
          <input
            type="number"
            value={incident.order ?? index}
            onChange={(e) => {
              const newOrder = parseInt(e.target.value) || 0;
              onReorder(incident.id, Math.max(0, Math.min(newOrder, totalIncidents - 1)));
            }}
            className={`w-12 px-2 py-1 text-center rounded text-sm ${
              isDark 
                ? 'bg-white/[0.05] border-white/10 text-white' 
                : 'bg-gray-50 border-gray-200 text-gray-900'
            } border`}
            min="0"
            max={totalIncidents - 1}
          />
        )}
      </td>
      <td className="px-6 py-4">
        <div>
          <div className={`text-sm font-medium ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {incident.title}
          </div>
          <div className={`text-sm ${
            isDark ? 'text-slate-400' : 'text-gray-500'
          }`}>
            {incident.description}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          getSeverityColor(incident.severity)
        }`}>
          {incident.severity.toUpperCase()}
        </span>
      </td>
      <td className={`px-6 py-4 whitespace-nowrap text-sm ${
        isDark ? 'text-slate-300' : 'text-gray-900'
      }`}>
        {formatTimeLimit(incident.timeLimit)}
      </td>
      <td className={`px-6 py-4 whitespace-nowrap text-sm ${
        isDark ? 'text-slate-300' : 'text-gray-900'
      }`}>
        {incident.category}
      </td>
      <td className={`px-6 py-4 whitespace-nowrap text-sm ${
        isDark ? 'text-slate-300' : 'text-gray-900'
      }`}>
        {incident.responseOptions?.length || 0}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        {!readOnly ? (
          <div className="flex items-center justify-end gap-2">
            <StandardButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit(incident);
              }}
              variant="ghost"
              size="small"
              title="Edit incident"
              className={`!p-2 ${
                isDark 
                  ? '!text-professional-blue-400 hover:!text-professional-blue-300' 
                  : '!text-blue-600 hover:!text-blue-700'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </StandardButton>
            <StandardButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(incident.id);
              }}
              variant="ghost"
              size="small"
              title="Delete incident"
              className={`!p-2 ${
                isDark 
                  ? '!text-red-400 hover:!text-red-300' 
                  : '!text-red-600 hover:!text-red-700'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </StandardButton>
          </div>
        ) : (
          <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            Library Incident
          </span>
        )}
      </td>
    </tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for better performance
  return (
    prevProps.incident.id === nextProps.incident.id &&
    prevProps.incident.title === nextProps.incident.title &&
    prevProps.incident.description === nextProps.incident.description &&
    prevProps.incident.severity === nextProps.incident.severity &&
    prevProps.incident.timeLimit === nextProps.incident.timeLimit &&
    prevProps.incident.category === nextProps.incident.category &&
    prevProps.incident.enabled === nextProps.incident.enabled &&
    prevProps.incident.order === nextProps.incident.order &&
    prevProps.index === nextProps.index &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.totalIncidents === nextProps.totalIncidents &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.onReorder === nextProps.onReorder
  );
});

IncidentTableRow.displayName = 'IncidentTableRow';